import type { Density, Theme } from '../theme';
import { AdministratorsView } from './AdministratorsView';
import { CompaniesView } from './CompaniesView';
import { CuratedDevicesView } from './CuratedDevicesView';
import { FrameworksView } from './FrameworksView';
import { HealthCard } from './HealthCard';
import { InviteUserView } from './InviteUserView';
import { RequestsInboxView } from './RequestsInboxView';
import { ShellLayout, type NavItem } from './ShellLayout';
import { UsersView } from './UsersView';
import { VendorDirectory } from './VendorDirectory';
import { VerificationQueueView } from './VerificationQueueView';
import { VerifiedCertificatesView } from './VerifiedCertificatesView';

/**
 * System Administrator console (FR-1/UX-DR9). Cross-tenant governance surface:
 * onboard Companies + invite users (Companies), manage Administrators (Story 6.3,
 * FR-30), reach the shared Vendor Directory (read-only browse) and the
 * Certification Catalog (FR-14), and the verification queue / verified
 * certificates. The Requests inbox (Story 6.4, FR-10/FR-12) curates
 * Manager-proposed records and onboards flagged Vendors. Devices (Story 8.5) is the
 * curated Device catalog, where an administrator records or corrects a device's
 * hardware/firmware revision — the only write path to a shared Device's fields (AD-6).
 */
export function AdminShell({
  theme,
  density,
  onToggleTheme,
  onToggleDensity,
}: {
  theme: Theme;
  density: Density;
  onToggleTheme: () => void;
  onToggleDensity: () => void;
}): React.JSX.Element {
  // Icons are per-item (`NavItem.icon`) rather than derived from the label: this shell's
  // vocabulary overlaps the cockpit's only partly ("Devices" yes, "Companies" no), so there
  // is no shared label→icon map to own. Where the same THING appears in two shells it gets
  // the same glyph — Vendor Directory and the cockpit's Vendors are both `package`, Devices
  // is `cpu` in both — so the sidebar reads consistently for someone who holds two roles.
  //
  // No count badges here. Every admin list is its own paginated fetch and this shell holds
  // none of their totals, so a badge would mean a request issued purely to decorate the nav.
  const nav: NavItem[] = [
    {
      label: 'Admin Console',
      icon: 'layout',
      content: (
        <>
          <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
            Admin Console
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            Cross-tenant governance. Onboard companies and invite users under Companies, manage
            platform Administrators, and browse the shared Vendor Directory and Certification
            Catalog.
          </p>
          <HealthCard />
        </>
      ),
    },
    {
      label: 'Companies',
      icon: 'building',
      content: (
        <>
          <CompaniesView />
          <InviteUserView />
        </>
      ),
    },
    { label: 'Administrators', icon: 'user-check', content: <AdministratorsView /> },
    { label: 'Users', icon: 'users', content: <UsersView /> },
    { label: 'Vendor Directory', icon: 'package', content: <VendorDirectory readOnly /> },
    // Story 8.5 (AD-25): the curated Device catalog's own tab. `DeviceDirectory` is
    // manager-mounted and reads the devices already INSTALLED on a fleet, so a curated
    // device nobody has installed — precisely the version-less population this tab
    // exists for — would be invisible there.
    { label: 'Devices', icon: 'cpu', content: <CuratedDevicesView /> },
    { label: 'Certification Catalog', icon: 'book', content: <FrameworksView /> },
    { label: 'Verification queue', icon: 'clock', content: <VerificationQueueView /> },
    { label: 'Verified certificates', icon: 'check-circle', content: <VerifiedCertificatesView /> },
    { label: 'Requests', icon: 'inbox', content: <RequestsInboxView /> },
  ];

  return (
    <ShellLayout
      roleLabel="System Administrator"
      navItems={nav}
      theme={theme}
      density={density}
      onToggleTheme={onToggleTheme}
      onToggleDensity={onToggleDensity}
    />
  );
}
